import ClearIcon from "@mui/icons-material/Clear";
import {
	Box,
	Checkbox,
	Chip,
	CircularProgress,
	FormControl,
	FormHelperText,
	IconButton,
	MenuItem,
	Select,
	Typography
} from "@mui/material";
import type { BaseSelectProps } from "@mui/material/Select";

import { forwardRef, useId } from "react";

export type SelectOption = { label: string; value: string | number; disabled?: boolean };

type OmittedSelectProps = "onChange" | "label" | "value" | "multiple";

type BaseProps = Omit<BaseSelectProps, OmittedSelectProps> & {
	label: string;
	options: SelectOption[];
	loading?: boolean;
	helperText?: string;
	placeholder?: string;
	onBlur?: () => void;
	clearable?: boolean;
};

type SingleSelectProps = BaseProps & {
	multiple?: false;
	value?: string | number | null;
	onChange?: (value: string | number | null) => void;
};

type MultipleSelectProps = BaseProps & {
	multiple: true;
	value?: (string | number)[];
	onChange?: (value: (string | number)[]) => void;
};

export type SelectInputProps = SingleSelectProps | MultipleSelectProps;

const SelectInput = forwardRef<HTMLDivElement, SelectInputProps>(
	(
		{
			label,
			options,
			value,
			onChange,
			onBlur,
			id,
			error,
			helperText,
			fullWidth,
			required,
			disabled,
			loading,
			multiple,
			placeholder,
			clearable,
			sx: sxProp,
			...selectProps
		},
		ref
	) => {
		const generatedId = useId();
		const inputId = id ?? generatedId;
		const isDisabled = disabled || loading;
		const hasValue = !multiple && value !== null && value !== undefined && value !== "";

		const handleChange = (e: { target: { value: unknown } }) => {
			if (!onChange) {
				return;
			}
			if (multiple) {
				(onChange as MultipleSelectProps["onChange"])!(e.target.value as (string | number)[]);
			} else {
				(onChange as SingleSelectProps["onChange"])!(
					e.target.value === "" ? null : (e.target.value as string | number)
				);
			}
		};

		return (
			<div style={{ width: fullWidth ? "100%" : "auto" }} ref={ref}>
				<Typography
					variant="text12Uppercase"
					component="label"
					htmlFor={inputId}
					sx={{
						display: "block",
						color: error ? "error.main" : "secondary.light50",
						mb: 0.5,
						textTransform: "uppercase"
					}}
				>
					{label} {required && "*"}
				</Typography>

				<Box sx={{ position: "relative", width: "100%" }}>
					<FormControl
						fullWidth={fullWidth ?? true}
						error={error}
						required={required}
						disabled={isDisabled}
					>
						<Select
							{...selectProps}
							size="small"
							id={inputId}
							value={multiple ? (value ?? []) : (value ?? "")}
							displayEmpty
							multiple={multiple}
							required={required}
							onChange={handleChange}
							onBlur={onBlur}
							sx={{
								...(sxProp as object),
								...(clearable && hasValue
									? { "& .MuiSelect-select": { pr: "52px !important" } }
									: {})
							}}
							renderValue={(selected) => {
								if (multiple) {
									const selectedValues = selected as (string | number)[];
									if (placeholder && selectedValues.length === 0) {
										return placeholder;
									}
									return (
										<Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
											{selectedValues.map((value) => (
												<Chip
													key={value}
													label={
														options.find((opt) => String(opt.value) === String(value))?.label ??
														String(value)
													}
													size="small"
													variant="filled"
													color="primary"
													sx={{ fontWeight: "normal", lineHeight: "20px" }}
												/>
											))}
										</Box>
									);
								} else {
									if (placeholder && (selected === "" || selected === null)) {
										return placeholder;
									}
									return (
										options.find((opt) => String(opt.value) === String(selected))?.label ??
										(selected as string | number)
									);
								}
							}}
						>
							{options.map((opt) => {
								const selected = multiple
									? (value as (string | number)[])?.some((v) => String(v) === String(opt.value))
									: String(value) === String(opt.value);

								return (
									<MenuItem key={`${opt.value}`} value={String(opt.value)} disabled={opt.disabled}>
										{multiple && <Checkbox checked={selected} sx={{ mr: 1 }} />}
										{opt.label}
									</MenuItem>
								);
							})}
						</Select>
						{helperText && <FormHelperText sx={{ mx: 0 }}>{helperText}</FormHelperText>}
					</FormControl>{" "}
					{clearable && hasValue && !isDisabled && (
						<IconButton
							size="small"
							aria-label="Clear"
							onClick={() => (onChange as SingleSelectProps["onChange"])!(null)}
							sx={{
								position: "absolute",
								top: "50%",
								right: 32,
								transform: "translateY(-50%)",
								zIndex: 1
							}}
						>
							<ClearIcon sx={{ fontSize: 16 }} />
						</IconButton>
					)}{" "}
					{loading && (
						<Box
							sx={{
								position: "absolute",
								top: "50%",
								right: 36,
								transform: "translateY(-50%)",
								zIndex: 1,
								pointerEvents: "none",
								display: "flex",
								alignItems: "center",
								justifyContent: "center"
							}}
						>
							<CircularProgress size={20} />
						</Box>
					)}
				</Box>
			</div>
		);
	}
);

SelectInput.displayName = "SelectInput";

export default SelectInput;
